import type { PublicCollection, PublicProduct, PublicVariant } from '@/types/commerce';

export function formatCurrency(amount: string | number | null | undefined, currency = 'INR') {
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }

  const numericAmount = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return null;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

export function formatDateLabel(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('en-IN', options).format(date);
}

export function formatTokenLabel(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatProductPriceRange(product: PublicProduct) {
  const currency = product.priceRange.currency ?? product.variants[0]?.currency ?? 'INR';
  const min = formatCurrency(product.priceRange.min, currency);
  const max = formatCurrency(product.priceRange.max, currency);

  if (min && max && min !== max) {
    return `${min} - ${max}`;
  }

  return min ?? max ?? 'Price on enquiry';
}

export function buildProductInquiryMessage(product: PublicProduct, variant?: PublicVariant | null) {
  const lines = ['Hello Sree Silks,', '', `I would like to enquire about "${product.title}".`];

  if (variant) {
    lines.push(`Preferred option: ${variant.label}.`);
  }

  if (product.collection?.name) {
    lines.push(`Collection: ${product.collection.name}.`);
  }

  lines.push('', `Product link: https://sreesilks.com/products/${product.slug}`);

  return lines.join('\n');
}

export function buildCollectionInquiryMessage(collection: PublicCollection) {
  return [
    'Hello Sree Silks,',
    '',
    `I would like to explore items from the "${collection.name}" collection.`,
    `Collection link: https://sreesilks.com/collections/${collection.slug}`,
  ].join('\n');
}

export function buildWhatsAppHref(phoneNumber: string, message: string) {
  const normalizedPhone = phoneNumber.replace(/[^\d]/g, '');
  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
}
